import { startCase } from 'lodash-es';
import type { ReactNode } from 'react';

import { VFlex, VStack, new__cn as cn } from '@/package';
import { VTooltip } from '@/package';

import type { PieChartData } from './type';

export const StatisticTable = <T extends PieChartData>({
  data,
  selectedValue,
  onRowClick,
}: {
  data: T[];
  selectedValue?: number | null;
  onRowClick?: (index: number) => void;
}) => {
  const totalObjects = data.reduce(
    (acc, item) => acc + item.aiCount + item.humanCount,
    0,
  );
  const dataWithRatio = data.map(item => ({
    ...item,
    ratio: Math.round(
      ((item.aiCount + item.humanCount) / (totalObjects || 1)) * 100,
    ),
  }));

  return (
    <VStack className="h-[242px] w-[534px]">
      <StatisticTableHeader />
      <StatisticTableBody
        data={dataWithRatio}
        selectedValue={selectedValue}
        onRowClick={onRowClick}
      />
      <StatisticTableFooter data={dataWithRatio} />
    </VStack>
  );
};

const StatisticTableHeader = () => {
  return (
    <TableRow className="w-full border-b border-gray-300 text-body-lg text-gray-400">
      <TableCell>Object class</TableCell>
      <TableCell className="w-full justify-end">Total objects</TableCell>
      <TableCell className="w-full justify-end">AI count</TableCell>
      <TableCell className="w-full justify-end">Human count</TableCell>
    </TableRow>
  );
};

const StatisticTableBody = <T extends PieChartData & { ratio: number }>({
  data,
  selectedValue,
  onRowClick,
}: {
  selectedValue?: number | null;
  onRowClick?: (index: number) => void;
  data: T[];
}) => {
  return (
    <VStack className="h-full max-h-[208px] flex-1 divide-y divide-gray-200 overflow-y-auto">
      {data.map((item, index) => {
        const isSelected = selectedValue === index;
        return (
          <TableRow
            key={item.name}
            className={cn(
              'h-[26px] min-h-[26px] w-full text-body-xl text-black',
              isSelected && 'bg-skyBlue-200',
              onRowClick && 'cursor-pointer',
            )}
            onClick={() => onRowClick?.(index)}
          >
            <TableCell className="max-w-[124px] text-body-lg text-gray-600">
              <div
                className="mr-1 inline-block size-3 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <VTooltip content={item.name}>
                <div className="truncate">{startCase(item.name)}</div>
              </VTooltip>
            </TableCell>
            <TableCell className="w-full text-body-lg text-gray-600">
              <VFlex items="center" justify="end" className="w-full" gap={0.25}>
                <span className="">{item.aiCount + item.humanCount}</span>
                <span className="h-2.5 w-px bg-gray-300" />
                <span className="inline-block w-10 text-right">
                  {item.ratio}%
                </span>
              </VFlex>
            </TableCell>
            <TableCell className="w-full justify-end text-body-lg text-gray-400">
              {item.aiCount}
            </TableCell>
            <TableCell className="w-full justify-end text-body-lg text-gray-400">
              {item.humanCount}
            </TableCell>
          </TableRow>
        );
      })}
    </VStack>
  );
};

const StatisticTableFooter = <T extends PieChartData>({
  data,
}: {
  data: T[];
}) => {
  const totalAiCount = data.reduce((acc, item) => acc + item.aiCount, 0);
  const totalHumanCount = data.reduce((acc, item) => acc + item.humanCount, 0);
  const totalObjects = totalAiCount + totalHumanCount;

  return (
    <TableRow className="w-full border-t border-gray-300 pt-2 text-body-lg text-gray-400">
      <TableCell className="font-bold text-gray-600">Total</TableCell>
      <TableCell className="text-right font-bold text-gray-600">
        <VFlex items="center" justify="end" className="w-full" gap={0.25}>
          <span>{totalObjects}</span>
          <span className="h-2.5 w-px bg-gray-300" />
          <span className="inline-block w-[39px] text-right">100%</span>
        </VFlex>
      </TableCell>
      <TableCell className="flex w-full justify-end text-right font-bold">
        {totalAiCount}
      </TableCell>
      <TableCell className="flex w-full justify-end text-right font-bold">
        {totalHumanCount}
      </TableCell>
    </TableRow>
  );
};

const TableRow = ({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_1.5fr_1fr_1fr] items-center [&>*]:h-full',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const TableCell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('flex w-full items-center px-2', className)}>
      {children}
    </div>
  );
};
